import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Formik } from 'formik'
import { startOfDay, subDays } from 'date-fns'
import { isEmpty } from 'lodash'

import { submitCoachingRequest } from '@/reducers/tasks/tasks.actions'
import { LOADING_PHRASES } from '@/reducers/tasks/tasks.constants'
import { CoachingSelfServiceForm } from './CoachingSelfServiceForm'

import './CoachingSelfService.scss'

export const CoachingSelfService = ({
  fetchTasksWithFilters,
  handleOpenTaskDrawer,
  suggestedAgents,
}) => {
  const dispatch = useDispatch()
  const [loadingPhraseIndex, setLoadingPhraseIndex] = useState(null)
  const [abortController, setAbortController] = useState(null)
  const intervalRef = useRef(null)
  const { organizationid: currentUserOrgId } = useSelector((state) => state.currentUser)
  const { loading, data } = useSelector((state) => state.tasks)
  const startDate = subDays(new Date(), 29)
  const endDate = startOfDay(new Date())
  const initialValues = {
    question: '',
    agent: null,
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
    channel: 'call',
    selectedPlaybooks: [],
  }

  const signalDone = () => {
    setAbortController(null)
  }

  const stopInterval = () => {
    clearInterval(intervalRef.current)
    intervalRef.current = null
  }

  const startInterval = () => {
    intervalRef.current = setInterval(() => {
      setLoadingPhraseIndex((prev) => prev + 1)
    }, 5000)
  }

  const handleResetInterval = () => {
    stopInterval()
    setLoadingPhraseIndex(null)
  }

  const handleSubmitCoachingRequest = async (values) => {
    if (abortController) {
      abortController.abort()
      signalDone()
      return
    }

    const newController = new AbortController()
    setAbortController(newController)

    const payload = {
      question: values.question,
      organization_id: currentUserOrgId,
      start_date: new Date(values.startDate).toISOString(),
      end_date: new Date(values.endDate).toISOString(),
      agents: [values.agent],
      call_duration: 1,
      call_types: [values.channel],
      ...(!isEmpty(values.selectedPlaybooks) && {
        playbook_cids: values.selectedPlaybooks.map((playbook) => playbook.value),
      }),
    }

    dispatch(
      submitCoachingRequest(
        payload,
        fetchTasksWithFilters,
        handleOpenTaskDrawer,
        handleResetInterval,
        newController.signal,
        signalDone
      )
    )
  }

  useEffect(() => {
    if (loading.coachingRequest) {
      setLoadingPhraseIndex(0)
    } else {
      handleResetInterval()
    }
  }, [loading.coachingRequest])

  useEffect(() => {
    if (loadingPhraseIndex === 0) {
      startInterval()
    }

    if (loadingPhraseIndex === LOADING_PHRASES.length - 1) {
      stopInterval()
    }
  }, [loadingPhraseIndex])

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        stopInterval()
      }
    }
  }, [])

  return (
    <div>
      <section className="coaching-selfservice" data-testid="coaching-selfservice">
        <Formik initialValues={initialValues} onSubmit={handleSubmitCoachingRequest}>
          {({ values, handleSubmit, setFieldValue }) => {
            return (
              <CoachingSelfServiceForm
                loadingPhraseIndex={loadingPhraseIndex}
                initialValues={initialValues}
                values={values}
                loading={loading}
                handleSubmit={handleSubmit}
                setFieldValue={setFieldValue}
                suggestedAgents={suggestedAgents}
                data={data}
              />
            )
          }}
        </Formik>
      </section>
    </div>
  )
}
